import {
  Actor,
  Body,
  Bracing,
  Casting,
  Dashing,
  Falling,
  Hitstun,
  Launch,
  Static,
  Transform,
  Velocity,
} from '@/components'
import type { Entity } from '@/core/ecs/Entity'
import type { SystemContext } from '@/core/ecs/Scheduler'
import type { World } from '@/core/ecs/World'
import { angleDamp, clamp } from '@/core/math/mathUtils'
import {
  BRACE,
  DI_MAX_ANGLE,
  LAUNCH_BOUNCE,
  LAUNCH_BRAKE,
  LAUNCH_GRAVITY,
  LAUNCH_SETTLE,
  LAUNCH_SKID,
  abilityDef,
} from '@/game/abilities/definitions'
import { Events } from '@/game/events'
import { EventsResource } from '@/game/resources'

const GRAVITY = 22
const FALL_SPIN = 5.5
/** Below this speed a character is considered to have regained its footing. */
const CONTROL_FULL_MARGIN = 1.02
const TURN_RATE = 16
/**
 * How fast sideways momentum is scrubbed while the stick is held, 1/s.
 *
 * At 14 the component of your speed that is not along the stick halves every
 * 50 ms, so a right-angle turn resolves in about a fifth of a second: enough
 * momentum to feel like a body, little enough to go where you point. This is
 * the number that decides whether the game feels like ice.
 */
const GRIP = 14
/** Extra acceleration when the stick points against your own velocity. */
const REVERSE_BRAKE = 1.7

/**
 * Directional influence: the victim may bend their own trajectory, but only by
 * `DI_MAX_ANGLE` in total and never by changing its speed.
 *
 * The old model gave the victim unbounded authority — the same Push produced
 * 6.44 m or 73.97 m depending on whether they held a stick, so the attacker
 * contributed less than a tenth of the outcome. Bounded influence keeps the
 * victim playing without letting them own the result.
 */
function applyDrift(
  world: World,
  entity: Entity,
  launch: ReturnType<typeof Launch.create>,
  actor: ReturnType<typeof Actor.create>,
  dt: number,
): void {
  const thrust = Math.hypot(actor.thrustX, actor.thrustZ)
  if (thrust < 0.2 || launch.di >= DI_MAX_ANGLE) return

  const vel = world.read(entity, Velocity)
  const speed = Math.hypot(vel.x, vel.z)
  if (speed < 1e-4) return

  // Only the component of the stick perpendicular to the flight bends it.
  const nx = vel.x / speed
  const nz = vel.z / speed
  const side = (actor.thrustX / thrust) * -nz + (actor.thrustZ / thrust) * nx
  const step = Math.min(DI_MAX_ANGLE - launch.di, Math.abs(side) * DI_RATE * dt)
  if (step <= 0) return

  launch.di += step
  const a = Math.sign(side) * step
  const cos = Math.cos(a)
  const sin = Math.sin(a)
  const x = vel.x
  vel.x = x * cos - vel.z * sin
  vel.z = x * sin + vel.z * cos
}

/** How fast the victim may spend their drift allowance, radians per second. */
const DI_RATE = 1.2

/**
 * Snapshots transforms at the start of every fixed step. The render stage
 * blends prev → current with `alpha`, which is what keeps 60 Hz physics smooth
 * on a 144 Hz display.
 */
export function transformSnapshotSystem({ world }: SystemContext): void {
  // Iterating the dense array directly is the cheapest possible pass — no
  // query, no entity indirection, and it runs on every transform in the world.
  const store = world.store(Transform)
  for (let i = 0; i < store.dense.length; i++) {
    const t = store.dense[i]
    t.prevX = t.x
    t.prevZ = t.z
    t.prevY = t.y
    t.prevAngle = t.angle
  }
}

/**
 * Integrates velocity and applies input thrust.
 *
 * The important subtlety: the speed cap is applied only to the component of
 * velocity *along the thrust direction*. Capping total speed would silently
 * delete knockback, and the entire game is about knockback.
 */
export function movementSystem({ world, dt }: SystemContext): void {
  const movers = world.query([Transform, Velocity, Body], { none: [Static] }).entities()

  for (const e of movers) {
    const tr = world.read(e, Transform)
    const vel = world.read(e, Velocity)
    const body = world.read(e, Body)
    const actor = world.get(e, Actor)
    const falling = world.get(e, Falling)

    if (falling) {
      // A negative timer is a stagger before the drop, used so scenery stranded
      // by the collapse does not all let go on the same frame.
      if (falling.timer < 0) {
        falling.timer += dt
        continue
      }

      // Free fall: horizontal momentum is kept, the body tumbles as it drops.
      vel.y -= GRAVITY * dt
      tr.y += vel.y * dt
      tr.x += vel.x * dt
      tr.z += vel.z * dt
      falling.timer += dt
      falling.spin += FALL_SPIN * dt
      tr.angle += FALL_SPIN * dt
      continue
    }

    // ── being launched ───────────────────────────────────────────────────
    //
    // A stone skipping across water: horizontal speed is untouched in the air
    // and is lost only on contact, together with the bounce. Nothing here reads
    // the victim's thrust except the bounded DI below, so the hit — not the
    // victim's stick — decides where they end up.
    const launch = actor ? world.get(e, Launch) : undefined
    if (actor && launch) {
      launch.elapsed += dt

      applyDrift(world, e, launch, actor, dt)

      if (launch.airborne) {
        vel.y -= LAUNCH_GRAVITY * dt
        tr.y += vel.y * dt

        if (tr.y <= 0) {
          tr.y = 0
          vel.y = -vel.y * LAUNCH_BOUNCE
          vel.x *= LAUNCH_SKID
          vel.z *= LAUNCH_SKID
          launch.bounces++

          const events = world.resource(EventsResource)
          events.emit(Events.Bounce, {
            entity: e,
            x: tr.x,
            z: tr.z,
            speed: Math.hypot(vel.x, vel.z),
            index: launch.bounces,
          })

          if (vel.y < LAUNCH_SETTLE) {
            vel.y = 0
            launch.airborne = false
          }
        }
      } else {
        // Grounded: brake hard and definitely, so the hit has an ending.
        const speed = Math.hypot(vel.x, vel.z)
        const next = Math.max(0, speed - LAUNCH_BRAKE * dt)
        if (next <= 0.01 || speed < 1e-5) {
          vel.x = 0
          vel.z = 0
          world.remove(e, Launch)
        } else {
          vel.x *= next / speed
          vel.z *= next / speed
        }
      }

      tr.x += vel.x * dt
      tr.z += vel.z * dt
      actor.gait += Math.hypot(vel.x, vel.z) * dt
      continue
    }

    const dash = world.get(e, Dashing)
    if (actor && dash) {
      // A dash overrides thrust entirely — fixed speed, fixed direction.
      vel.x = dash.dirX * dash.speed
      vel.z = dash.dirZ * dash.speed
      tr.angle = Math.atan2(dash.dirX, dash.dirZ)
      tr.x += vel.x * dt
      tr.z += vel.z * dt
      actor.gait += dash.speed * dt
      continue
    }

    if (actor) {
      const speed = Math.hypot(vel.x, vel.z)
      const braced = world.has(e, Bracing)

      // Being thrown around costs input authority: at twice your top speed you
      // keep only a quarter of it. This is what makes a big hit feel decisive.
      const overspeed = speed / Math.max(actor.maxSpeed, 0.001)
      actor.control =
        overspeed <= CONTROL_FULL_MARGIN
          ? 1
          : clamp(1 - (overspeed - CONTROL_FULL_MARGIN) * 0.75, 0.25, 1)

      // During hitstun the fighter has no authority at all: they are being
      // thrown, and a hit that can be steered out of on the same frame does not
      // read as a hit.
      const stunned = world.has(e, Hitstun)
      const thrustMag = stunned ? 0 : Math.hypot(actor.thrustX, actor.thrustZ)

      // Casting costs something. Previously this system never read `Casting` at
      // all, so an attack kept full speed and full turning for its whole
      // animation — throwing one was free, and free attacks are what "mashing"
      // means. See docs/PHYSICS.md §6.3.
      const cast = world.get(e, Casting)
      let castAuthority = 1
      let turnLocked = false
      if (cast) {
        const def = abilityDef(cast.ability)
        if (cast.phase === 'windup') {
          castAuthority = 0.5
          turnLocked = true
        } else if (cast.phase === 'active') {
          turnLocked = true
          if (def.lunge > 0) {
            // A lunge commits the body: it plants you inside your opponent's
            // reach, so a miss can be punished.
            vel.x = Math.sin(tr.angle) * def.lunge
            vel.z = Math.cos(tr.angle) * def.lunge
          }
          castAuthority = def.lunge > 0 ? 0 : 0.4
        } else {
          castAuthority = def.recoverAuthority
        }
      }

      const authority = actor.control * (braced ? BRACE.speedMul : 1) * castAuthority

      const cap = actor.maxSpeed * (braced ? BRACE.speedMul : 1)

      if (thrustMag > 0.01) {
        const nx = actor.thrustX / thrustMag
        const nz = actor.thrustZ / thrustMag
        const accel = actor.accel * authority * Math.min(thrustMag, 1)

        // Grip: the part of your velocity that is *not* along the stick bleeds
        // off while you are holding it.
        //
        // Without this the only thing steering did was add a vector: push left
        // while running right and you kept every bit of the rightward speed,
        // arcing across the platform instead of turning on it, because friction
        // was applied *only when there was no input at all*. That is the whole
        // of "как будто по льду скользишь", and no amount of raising friction
        // could fix it — friction was never running while you were moving.
        //
        // Momentum along the stick is untouched, so a run still has weight and
        // a knockback still cannot be steered out of: `authority` is already
        // cut to a quarter at twice top speed and to zero during hitstun.
        const along = vel.x * nx + vel.z * nz
        const sideX = vel.x - nx * along
        const sideZ = vel.z - nz * along
        const grip = Math.exp(-GRIP * authority * dt)
        vel.x = nx * along + sideX * grip
        vel.z = nz * along + sideZ * grip

        // Turning back on yourself brakes harder than starting from a stop: a
        // reversal should read as a decision, not as a long skid.
        const reversing = along < 0
        const push = reversing ? accel * REVERSE_BRAKE : accel

        vel.x += nx * push * dt
        vel.z += nz * push * dt

        // Thrust may steer, but never add speed beyond what you already had.
        const limit = Math.max(speed, cap)
        const after = Math.hypot(vel.x, vel.z)
        if (after > limit && after > 1e-6) {
          const scale = limit / after
          vel.x *= scale
          vel.z *= scale
        }

        if (!turnLocked) tr.angle = angleDamp(tr.angle, Math.atan2(nx, nz), TURN_RATE, dt)
      }
      // A fighter no longer turns to face the way they are travelling: the game
      // used to rotate you *away* from whoever was pushing you.

      // Residual speed above your own maximum bleeds off. Launches no longer
      // reach this branch — they are handled as a state above — so this only
      // cleans up shoves, bumps and pulls.
      const current = Math.hypot(vel.x, vel.z)
      if (current > cap) {
        const excess = (current - cap) * Math.exp(-body.drag * dt)
        const scale = (cap + excess) / current
        vel.x *= scale
        vel.z *= scale
      } else if (thrustMag <= 0.01 && !stunned) {
        const friction = braced ? actor.friction * 2.5 : actor.friction
        const decay = Math.exp(-friction * dt)
        vel.x *= decay
        vel.z *= decay
      }

      actor.gait += Math.hypot(vel.x, vel.z) * dt
    } else {
      // Props: pure drag, no self-propulsion.
      const decay = Math.exp(-body.drag * dt)
      vel.x *= decay
      vel.z *= decay
      const speed = Math.hypot(vel.x, vel.z)
      if (speed > 0.15) tr.angle += speed * dt * 0.9
      else {
        vel.x = 0
        vel.z = 0
      }
    }

    tr.x += vel.x * dt
    tr.z += vel.z * dt
  }
}
